import React, { useState, useRef, useEffect } from 'react';
import { Rnd } from 'react-rnd';
import clsx from 'clsx';

const ICONS = {
  close: (
    <svg width="8" height="8" viewBox="0 0 8 8" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M1 1L7 7M7 1L1 7" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  ),
  minimize: (
    <svg width="8" height="8" viewBox="0 0 8 8" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M1 4H7" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  ),
  maximize: (
    <svg width="8" height="8" viewBox="0 0 8 8" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M2 2V6H6V2H2Z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
    </svg>
  ),
  restore: (
    <svg width="8" height="8" viewBox="0 0 8 8" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M1 3V7H5V3H1Z M3 1H7V5" stroke="currentColor" strokeWidth="1.2" fill="none" strokeLinejoin="round" />
    </svg>
  ),
};

export default function Window({
  id,
  title,
  icon,
  isOpen = true,
  isMinimized = false,
  isMaximized = false,
  zIndex = 100,
  position = { x: 80, y: 60 },
  size = { width: 720, height: 480 },
  minSize = { width: 360, height: 240 },
  onClose,
  onMinimize,
  onMaximize,
  onFocus,
  onMove,
  onResize,
  children,
  className = '',
  tone = 'cyan',
  resizable = true,
  draggable = true,
  closable = true,
  minimizable = true,
  maximizable = true,
}) {
  const [hovered, setHovered] = useState(false);
  const [visible, setVisible] = useState(false);
  const rndRef = useRef(null);

  // fade-in on mount via CSS
  useEffect(() => {
    const id = requestAnimationFrame(() => setVisible(true));
    return () => cancelAnimationFrame(id);
  }, []);

  const reducedMotion = typeof window !== 'undefined'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (!isOpen || isMinimized) return null;

  const toneCls = {
    cyan: 'border-[var(--neon-cyan)]/40 shadow-[0_0_30px_rgba(0,240,255,0.15)]',
    pink: 'border-[var(--neon-pink)]/40 shadow-[0_0_30px_rgba(255,42,109,0.15)]',
    violet: 'border-[var(--neon-violet)]/40 shadow-[0_0_30px_rgba(168,85,247,0.15)]',
    green: 'border-[var(--neon-green)]/40 shadow-[0_0_30px_rgba(57,255,20,0.15)]',
  }[tone];

  const titleAccent = {
    cyan: 'text-[var(--neon-cyan)]',
    pink: 'text-[var(--neon-pink)]',
    violet: 'text-[var(--neon-violet)]',
    green: 'text-[var(--neon-green)]',
  }[tone];

  const dotColor = {
    cyan: 'bg-[var(--neon-cyan)]',
    pink: 'bg-[var(--neon-pink)]',
    violet: 'bg-[var(--neon-violet)]',
    green: 'bg-[var(--neon-green)]',
  }[tone];

  // Botões traffic-light no padrão macOS — formato pílula alongada (28x12px)
  // com label textual SEMPRE visível (não só em hover). Garante acessibilidade
  // e elimina ambiguidade sobre o que cada botão faz.
  const TrafficButton = ({ color, hoverColor, onClick, ariaLabel, children }) => (
    <button
      type="button"
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => { e.stopPropagation(); onClick && onClick(); }}
      className={clsx(
        'group relative flex items-center justify-center w-3.5 h-3.5 rounded-full text-black/80',
        'transition-all duration-150 select-none z-20 pointer-events-auto cursor-pointer border border-black/20',
        color, hoverColor
      )}
      aria-label={ariaLabel}
      title={ariaLabel}
    >
      <span className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
        {children}
      </span>
    </button>
  );

  // NÃO colocar `transform` no style do Rnd — react-draggable usa
  // transform:translate(x,y) pra mover. scale/transition no mesmo nó = drag morto.
  const fadeStyle = reducedMotion
    ? { opacity: visible ? 1 : 0 }
    : {
        opacity: visible ? 1 : 0,
        transform: visible ? 'scale(1)' : 'scale(0.97)',
        transition: 'opacity 0.15s ease-out, transform 0.15s ease-out',
      };

  return (
    <Rnd
      ref={rndRef}
      position={isMaximized ? { x: 0, y: 0 } : { x: position.x, y: position.y }}
      size={isMaximized ? { width: '100%', height: '100%' } : { width: size.width, height: size.height }}
      minWidth={minSize.width}
      minHeight={minSize.height}
      bounds="parent"
      dragHandleClassName="window-drag-handle"
      cancel="button, a, input, textarea, select"
      axis="both"
      disableDragging={!draggable || isMaximized}
      enableResizing={resizable && !isMaximized}
      onDragStart={() => {
        onFocus && onFocus(id);
      }}
      onDrag={(e, d) => {
        onMove && onMove(id, { x: d.x, y: d.y });
      }}
      onDragStop={(e, d) => {
        onMove && onMove(id, { x: d.x, y: d.y });
      }}
      onResizeStop={(_, __, ref, ___, pos) => {
        onResize && onResize(id, {
          w: parseInt(ref.style.width, 10),
          h: parseInt(ref.style.height, 10),
        }, pos);
      }}
      style={{
        zIndex,
        pointerEvents: 'auto',
      }}
      className="window-root"
    >
      <div
        role="dialog"
        aria-modal="false"
        aria-label={title}
        style={fadeStyle}
        className={clsx(
          'forge-window flex flex-col w-full h-full overflow-hidden transition-shadow',
          toneCls,
          hovered && 'shadow-[0_0_40px_rgba(0,240,255,0.3)]',
          className
        )}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onMouseDown={(e) => console.log('[WINDOW ONMOUSEDOWN]', e.target.className)}
      >
        {/* Title bar */}
        <div
          className="window-drag-handle forge-window-header cursor-grab active:cursor-grabbing select-none"
          onDoubleClick={() => maximizable && onMaximize && onMaximize(id)}
        >
            <div className="flex items-center gap-2 min-w-0">
            {/* Traffic lights macOS-style (pílulas com label) */}
            <div className="flex items-center gap-1 shrink-0">
              {closable && (
                <TrafficButton
                  color="bg-[#ff5f57]"
                  hoverColor="hover:bg-[#ff3b30]"
                  label="FECHAR"
                  ariaLabel="Fechar janela"
                  onClick={() => onClose && onClose(id)}
                >
                  {ICONS.close}
                </TrafficButton>
              )}
              {minimizable && (
                <TrafficButton
                  color="bg-[#febc2e]"
                  hoverColor="hover:bg-[#f5a623]"
                  label="MIN"
                  ariaLabel="Minimizar janela"
                  onClick={() => onMinimize && onMinimize(id)}
                >
                  {ICONS.minimize}
                </TrafficButton>
              )}
              {maximizable && (
                <TrafficButton
                  color="bg-[#28c840]"
                  hoverColor="hover:bg-[#1da834]"
                  label={isMaximized ? 'REST' : 'MAX'}
                  ariaLabel={isMaximized ? 'Restaurar janela' : 'Maximizar janela'}
                  onClick={() => onMaximize && onMaximize(id)}
                >
                  {isMaximized ? ICONS.restore : ICONS.maximize}
                </TrafficButton>
              )}
            </div>
            {icon && <span className="forge-window-icon">{icon}</span>}
            <span className="forge-window-title truncate">
              {title}
            </span>
          </div>

          <div className="flex items-center gap-1.5 shrink-0">
            <span className="forge-window-meta">FILE</span>
          </div>
        </div>

        <div className="flex-1 overflow-auto bg-gradient-to-br from-[var(--bg-deep)]/50 to-[var(--bg-primary)]/30">
          {children}
        </div>

        {resizable && !isMaximized && (
          <div className="absolute bottom-0 right-0 w-4 h-4 cursor-nwse-resize pointer-events-none z-10">
            <svg viewBox="0 0 16 16" className="w-full h-full">
              <path d="M14 2L2 14M14 7L7 14M14 12L12 14" stroke="rgba(0,240,255,0.5)" strokeWidth="1.2" fill="none" />
            </svg>
          </div>
        )}
      </div>
    </Rnd>
  );
}
